import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {uuidv7} from '../api';
import type {ChannelMenuItem, Modifier, NewOrderLine, Satang} from '../api/types';
import {StorageKeys, readJson, writeJson} from '../app/storage';
import {useMenu} from '../menu';
import {useGuestSession} from '../session';
import {
  EMPTY_CART,
  acceptPrice,
  addLine,
  estimateSubtotalSatang,
  findCartIssues,
  itemCount,
  removeLine,
  setQuantity,
  toNewOrderLines,
  type CartLine,
  type CartLineIssue,
  type CartModifier,
  type CartState,
} from './cartModel';

/**
 * The cart.
 *
 * Local, persisted, and **not** synced to the server before submission. That is
 * a consequence of the endpoint set the guest surface has: `POST /v1/orders`
 * creates an order from the lines it is given, and appending to an order needs
 * the order-event endpoint. So the cart is client state right up until the
 * moment the guest commits, and the server prices it then.
 *
 * It survives a reload because a guest who reloads mid-meal and finds an empty
 * cart waves at a server instead, which is the labour saving this feature
 * exists to produce.
 *
 * Switching language does not touch it (03 §Q2 requires exactly that): the cart
 * stores `LocalizedText` snapshots, not rendered strings, so all three languages
 * read the same cart.
 */

export interface AddItemInput {
  item: ChannelMenuItem;
  quantity?: number;
  /** Chosen modifiers, as the objects from the item's own modifier groups. */
  modifiers?: {modifier: Modifier; quantity?: number}[];
  note?: string | null;
}

export interface CartValue {
  lines: CartLine[];
  /** Total number of things, not number of rows. */
  count: number;
  /** Menu-price estimate. **Not the bill** — see `cartModel.ts`. */
  estimatedSubtotalSatang: Satang;
  /** Sold-out, vanished and repriced lines, live against the current menu. */
  issues: CartLineIssue[];
  /** True when nothing in the cart blocks submission. */
  submittable: boolean;
  add: (input: AddItemInput) => CartLine;
  setQuantity: (lineId: string, quantity: number) => void;
  remove: (lineId: string) => void;
  /** Clear a `reprice` issue by taking the new price. */
  acceptNewPrice: (lineId: string, unitPriceSatang: Satang) => void;
  /** Drop every sold-out and vanished line at once. Q5's recovery action. */
  removeUnavailable: () => void;
  clear: () => void;
  /** `SubmitOrderRequest.lines`. Ids and quantities only — no money. */
  toOrderLines: () => NewOrderLine[];
}

const CartContext = createContext<CartValue | null>(null);

/// The **guest session** a cart belongs to, or null when there is no live one.
///
/// Keyed by `guest_session_id` and not by table, because a table's QR is
/// regenerated between sittings: a new customer scanning a fresh token for the
/// same table 2 gets a new `guest_session_id` but the *same* `table_id`, and a
/// table-keyed owner read the two as one sitting and carried the previous
/// customer's cart into the new order (owner, 2026-08-19). The session id is
/// minted once per `open()` and returned unchanged by resume/refresh, so a
/// genuine mid-order reload keeps its cart while any new token — new table, new
/// shop, or a new sitting on the same table — starts empty. It is also the only
/// key that separates two queue-ahead sittings, where `table_id` is null.
function cartOwnerOf(session: {guest_session_id: string} | null): string | null {
  return session ? session.guest_session_id : null;
}

export function CartProvider({children}: {children: ReactNode}) {
  const {itemsById, isAvailable, status: menuStatus} = useMenu();
  const {session} = useGuestSession();
  const [state, setState] = useState<CartState>(
    () => readJson<CartState>(StorageKeys.cart) ?? EMPTY_CART,
  );

  useEffect(() => {
    writeJson(StorageKeys.cart, state);
  }, [state]);

  // Keep the cart only when it belongs to the active guest *session*. Its lines
  // are menu snapshots of one sitting, so carrying them into a re-scan — a
  // different table, a different shop, a **new token on the same table**, or a
  // **stale cart left by an earlier sitting that carries no ownership tag at
  // all** — is the "another shop's ฿60 is stuck on my empty table 3" bug (owner,
  // 2026-08-17/18) and the "last customer's items bleed into the next scan"
  // bug (owner, 2026-08-19). Keying by session id rather than by table is what
  // catches that last one: a regenerated QR keeps the table but mints a new
  // session.
  //
  // The tag is written the moment a session goes active, so a genuine mid-order
  // cart always carries the matching owner and survives a refresh; a cart whose
  // owner does not match the live session — **null included** — belonged to some
  // other sitting and is cleared. The one cost is a guest who was mid-order at
  // the exact instant this shipped, whose untagged cart is reset once.
  const cartOwner = cartOwnerOf(session ?? null);
  const cartOwnerRef = useRef<string | null>(readJson<string>(StorageKeys.cartOwner) ?? null);
  useEffect(() => {
    if (!cartOwner || cartOwnerRef.current === cartOwner) return;
    setState(EMPTY_CART);
    cartOwnerRef.current = cartOwner;
    writeJson(StorageKeys.cartOwner, cartOwner);
  }, [cartOwner]);

  const add = useCallback((input: AddItemInput): CartLine => {
    const modifiers: CartModifier[] = (input.modifiers ?? []).map(
      ({modifier, quantity}) => ({
        modifier_id: modifier.modifier_id,
        name_snapshot: modifier.name,
        price_delta_satang: modifier.price_delta_satang,
        quantity: quantity ?? 1,
      }),
    );

    const line: CartLine = {
      line_id: uuidv7(),
      item_id: input.item.item_id,
      name_snapshot: input.item.name,
      // The effective channel price, snapshotted. Never re-derived.
      unit_price_satang: input.item.price_satang,
      quantity: input.quantity ?? 1,
      modifiers,
      note: input.note ?? null,
    };

    setState((current) => addLine(current, line));
    return line;
  }, []);

  /**
   * Issues are only derivable against a menu that arrived.
   *
   * `findCartIssues` asks the projection about every line, and an absent
   * projection answers "not in the menu" for all of them — so while the menu is
   * still loading, or after a first read failed, **every line in the cart looks
   * `gone`**. Q5 then offers to delete the whole cart and Q7 strikes every line
   * through and replaces send with "back to cart", which is a loop the guest
   * cannot leave: nothing in the cart is wrong, so nothing they remove fixes it,
   * and the send button never comes back.
   *
   * `MenuProvider` keeps `status` at `'ready'` through a failed *background*
   * refresh precisely so a menu already on screen is not blanked, so `ready` is
   * exactly "there is a projection to compare against". Anything else means this
   * client does not know yet — and not knowing is not evidence that a dish sold
   * out. Submitting under that ignorance is safe: the server refuses with
   * `item_unavailable` and Q7 marks those lines from the refusal itself.
   */
  const issues = useMemo(
    () =>
      menuStatus === 'ready'
        ? findCartIssues(state, {
            item: (itemId) => itemsById.get(itemId),
            isAvailable,
          })
        : [],
    [state, itemsById, isAvailable, menuStatus],
  );

  const value = useMemo<CartValue>(
    () => ({
      lines: state.lines,
      count: itemCount(state),
      estimatedSubtotalSatang: estimateSubtotalSatang(state),
      issues,
      // A repriced line is not a blocker — the guest acknowledges it and the
      // server charges the real number either way. A sold-out line is, because
      // submitting it produces `item_unavailable` at the worst moment.
      submittable:
        state.lines.length > 0 && !issues.some((issue) => issue.kind !== 'reprice'),
      add,
      setQuantity: (lineId, quantity) =>
        setState((current) => setQuantity(current, lineId, quantity)),
      remove: (lineId) => setState((current) => removeLine(current, lineId)),
      acceptNewPrice: (lineId, unitPriceSatang) =>
        setState((current) => acceptPrice(current, lineId, unitPriceSatang)),
      removeUnavailable: () =>
        setState((current) =>
          issues
            .filter((issue) => issue.kind !== 'reprice')
            .reduce((next, issue) => removeLine(next, issue.line.line_id), current),
        ),
      clear: () => setState(EMPTY_CART),
      toOrderLines: () => toNewOrderLines(state),
    }),
    [state, issues, add],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartValue {
  const value = useContext(CartContext);
  if (!value) throw new Error('useCart must be used inside <CartProvider>');
  return value;
}
